import { inject, Injectable } from '@angular/core';
import { Indicator } from '../../models/indicator';
import { IndicatorData } from '../../models/indicator-data';
import { GeoDataStore } from '../../stores/geo/geo-data.state';
import { CategoryStore } from '../../stores/category/category.state';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { GlobalsStore } from '../../stores/globals.state';
import { DataLayerStore } from '../../stores/data-layer/data-layer.state';
import { Category } from '../../models/category';
import { DataLayer } from '../../models/data-layer';
import { ComparisonValueStore } from '../../stores/comparison-value/comparison-value.state';
import { ComparisonValue } from '../../models/geo-data';

export interface ExportSheet {
  title: string;
  rows: string[][];
}

/**
 * Enum to define the available data export modes.
 */
export enum DataExportMode {
  CustomIndicators = 'customIndicators',
  IncludeEmpty = 'includeEmpty',
  ExcludeEmpty = 'excludeEmpty',
  MultipleYears = 'multipleYears',
  published = 'published',
}

@Injectable({
  providedIn: 'root',
})
export class ExportHelperService {
  readonly globalsStore = inject(GlobalsStore);
  readonly geoStore = inject(GeoDataStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly comparisonStore = inject(ComparisonValueStore);

  layerData: { [key: string]: IndicatorData } = {};
  features: GeoJSON.Feature[] = [];
  comparisonValues: ComparisonValue[] = [];
  currentFeature?: GeoJSON.Feature;
  currentCompValue?: ComparisonValue;
  rows: string[][] = [];
  currentRow: string[] = [];
  sheets: ExportSheet[] = [];
  mode: DataExportMode = DataExportMode.CustomIndicators;
  captions: string[][] = [];

  reset() {
    this.features = [];
    this.comparisonValues = [];
    this.currentFeature = undefined;
    this.rows = [];
    this.currentRow = [];
    this.sheets = [];
    this.indicatorStore.clearFilter();
  }

  /**
   * Builds an array of export sheets based on the selected data export mode, sorting preferences, and order.
   *
   * @param {DataExportMode} mode - Specifies the mode of data export, determining the structure of the exported data.
   * @param {'key' | 'name'} [sorting] - Optional parameter to define the sorting type. Either by 'key' or 'name'.
   * @param {'asc' | 'desc'} [order] - Optional parameter to specify the sorting order. Either in ascending ('asc') or descending ('desc') order.
   * @return {ExportSheet[]} Returns an array of ExportSheet objects representing the prepared data for export.
   */
  buildIndicatorDataExportArray(
    mode: DataExportMode,
    sorting?: 'key' | 'name',
    order?: 'asc' | 'desc'
  ): ExportSheet[] {
    this.reset();
    this.captions = this.generateCaptions(
      mode === DataExportMode.MultipleYears
    );
    this.comparisonValues = this.comparisonStore.withoutDefaultAsArray();

    this.features = sorting
      ? this.geoStore.getFeaturesSorted(sorting, order)
      : this.geoStore.featuresFromSelection();

    this.mode = mode;
    if (mode === 'multipleYears') {
      this.getRowsByYears();
    } else {
      // group by category
      this.setLayerData();
      this.getRowsByTree(mode);
    }

    return this.sheets;
  }

  /**
   * Generates rows based on a tree structure and exports them into sheets.
   *
   * @param {DataExportMode} mode - The mode which determines how indicators are filtered and exported.
   * @return {void} This method does not return a value but populates the sheets array with formatted rows and metadata.
   */
  getRowsByTree(mode: DataExportMode): void {
    const flatTree = this.categoryStore.asFlatTree();

    for (const idx in flatTree) {
      if (flatTree[idx]) {
        this.rows = [];
        const category: Category = flatTree[idx];
        const categoryIndicatorIds: string[] = this.getIndicatorCategoryIds(
          category._id
        );
        const indicatorsToExport: string[] = this.filterIndicatorsByMode(
          categoryIndicatorIds,
          mode
        );
        const indicatorsAndChildrenToExport: Indicator[] =
          this.handleIndicatorSets(indicatorsToExport);

        if (indicatorsAndChildrenToExport.length) {
          this.handleSingleYearRows(indicatorsAndChildrenToExport);
          // Adds path to first cell of each sheet
          //rows[0][0] = category.path
          //  ? `${category.path}${category.name}`
          //  : category.name;
          this.sheets.push({
            title: `${category.number} ${category.name.replace(/([*?:\\/[\]])/g, '_').substring(0, 30)}`,
            rows: this.rows,
          });
        }
      }
    }
  }

  /**
   * Processes and organizes indicators by years to generate rows and sheets for export.
   *
   * @return {void} Does not return any value. The method updates internal state by processing
   * indicators, generating rows for each year, and adding them to the sheets array.
   */
  getRowsByYears(): void {
    const indicatorsToExport = this.getIndicatorIdsBySelection();
    const indicatorsAndChildrenToExport: Indicator[] =
      this.handleIndicatorSets(indicatorsToExport);

    indicatorsAndChildrenToExport.forEach(
      (indicator: Indicator, idx: number) => {
        this.rows = [];
        this.handleMultipleYearRows(indicator);
        this.rows[0][0] = indicator.key;
        // single set children stand on their own - prefix the set name
        const name = this.indicatorStore.getFullName(indicator, false);
        this.rows[0][1] = name;
        this.sheets.push({
          title: `${idx + 1} - ${name.replace(/([*?:\\/[\]])/g, '_').substring(0, 30)}`,
          rows: this.rows,
        });
      }
    );
  }

  /**
   * Resolves indicator ids to the indicators to export. Sets have no data of their
   * own: each set is replaced by those of its children which are part of `indicatorIds`
   * (in set order). Children are exported on their own as well, so single children
   * of a set can be exported. Independent of the current store filter.
   *
   * @param {string[]} indicatorIds - An array of indicator IDs to process.
   * @return {Indicator[]} The indicators to export, without sets and duplicates.
   */
  handleIndicatorSets(indicatorIds: string[]): Indicator[] {
    const idSet = new Set(indicatorIds);
    const groupedChildren = this.indicatorStore.allGroupedChildren();
    const result: Indicator[] = [];
    const added = new Set<string>();
    const add = (indicator: Indicator) => {
      if (added.has(indicator._id)) return;
      added.add(indicator._id);
      result.push(indicator);
    };

    for (const indicatorId of indicatorIds) {
      const indicator = this.indicatorStore.getById(indicatorId);
      if (!indicator) continue;

      if (indicator.isSet) {
        (groupedChildren[indicator._id] || [])
          .filter(child => idSet.has(child._id))
          .forEach(add);
      } else if (!indicator.parent || !idSet.has(indicator.parent)) {
        // children of listed sets are added right after their set
        add(indicator);
      }
    }

    return result;
  }

  /**
   * Filters indicator IDs based on the specified data export mode.
   *
   * @param {string[]} sheetIndicatorIds - An array of indicator IDs to be filtered.
   * @param {DataExportMode} mode - The mode used to determine how indicators are filtered.
   * @return {string[]} An array of filtered indicator IDs based on the selected mode.
   */
  filterIndicatorsByMode(
    sheetIndicatorIds: string[],
    mode: DataExportMode
  ): string[] {
    switch (mode) {
      case DataExportMode.CustomIndicators:
        return this.getIndicatorsByStoreSelection(sheetIndicatorIds);
      case DataExportMode.IncludeEmpty:
        return sheetIndicatorIds ? [...sheetIndicatorIds] : [];
      case DataExportMode.published:
      case DataExportMode.ExcludeEmpty:
        return this.getFilledIndicators(sheetIndicatorIds);
      default:
        return this.getEmptyIndicators(sheetIndicatorIds);
    }
  }

  /**
   * Retrieves an array of indicator IDs filtered by the specified category ID.
   *
   * @param {string} categoryId - The ID of the category used to filter indicators.
   * @return {string[]} An array of indicator IDs that belong to the specified category.
   */
  getIndicatorCategoryIds(categoryId: string): string[] {
    return this.indicatorStore
      .filterByCategory(categoryId)
      .map(indicator => indicator._id);
  }

  /**
   * Sets the layer data*/
  private setLayerData(layerId?: string): void {
    const selectedLayer = this.globalsStore.selectedLayer();
    if (this.mode === DataExportMode.published) {
      this.indicatorDataStore.updateFilter({ published: true });
    }
    const groupedLayerData = this.indicatorDataStore.groupByLayerAndIndicator();
    if (selectedLayer && groupedLayerData) {
      this.layerData = groupedLayerData[layerId || selectedLayer._id];
    }
  }

  /**
   * Filters the provided indicator IDs and returns an array of IDs that are present in the layer data.
   *
   * @param {string[]} indicatorIds - An array of indicator IDs to filter.
   * @return {string[]} An array of indicator IDs that are found in the layer data.
   */
  getFilledIndicators(indicatorIds: string[]): string[] {
    const layerData = this.layerData;
    return layerData && indicatorIds
      ? indicatorIds.filter(id => layerData && layerData[id])
      : [];
  }

  /**
   * Returns an array of indicator IDs that do not have corresponding data in the layer data.
   *
   */
  getEmptyIndicators(indicatorIds: string[]): string[] {
    const layerData = this.layerData;
    return indicatorIds
      ? indicatorIds.filter(id => !layerData || !layerData[id])
      : [];
  }

  /**
   * Filters and retrieves the subset of indicators based on the provided store selection.
   *
   * @param {string[]} indicatorIds - An array of indicator IDs to filter.
   * @return {string[]} A filtered array of indicator IDs that match the current store selection.
   */
  getIndicatorsByStoreSelection(indicatorIds: string[]): string[] {
    const filterByIndicatorIds = this.getIndicatorIdsBySelection();
    return indicatorIds.filter(id => filterByIndicatorIds.includes(id));
  }

  /**
   * Retrieves an array of indicator IDs based on the current selection in the indicator store.
   *
   * @return {string[]} An array of indicator IDs derived from the selected indicators.
   */
  getIndicatorIdsBySelection(): string[] {
    return [...this.indicatorStore.selection()].map(indicator => {
      return indicator._id;
    });
  }

  /**
   * Generates a captions array based on whether multiple years are being considered.
   *
   * @param {boolean} multipleYears - Indicates if the captions should be generated for multiple years or a single year.
   * @return {string[][]} A two-dimensional array containing captions for the relevant configuration*/
  generateCaptions(multipleYears: boolean): string[][] {
    if (multipleYears) {
      return [
        ['', ''],
        //['SRID', 'Sozialraum'],
        ['Durchschnitt', ''],
        ['Gesamtwert', ''],
      ];
    } else {
      return [
        ['', ''],
        ['SRID', 'Sozialraum'],
        ['Durchschnitt', ''],
        ['Gesamtwert', ''],
      ];
    }
  }

  /**
   * Processes and handles rows for a single year export.
   *
   * @param {Indicator[]} itemsToExport - An array of indicators to export for the single year data handling process.
   * @return {void} This method does not return a value.
   */
  handleSingleYearRows(itemsToExport: Indicator[]): void {
    const exportRowLength = this.features.length + this.comparisonValues.length;
    for (let rowIdx = -4; rowIdx < exportRowLength; rowIdx++) {
      this.initializeRow(rowIdx);
      for (const indicator of itemsToExport) {
        this.handleRow(indicator, rowIdx);
      }
      this.rows.push(this.currentRow);
    }
  }

  /**
   * Handles the processing of multiple year rows for the given indicator.
   * Iterates through layers and rows to initialize and populate data accordingly.
   *
   * @param {Indicator} indicator - The indicator used to determine data processing logic.
   * @return {void} This method does not return a value.
   */
  handleMultipleYearRows(indicator: Indicator): void {
    const exportRowLength = this.features.length + this.comparisonValues.length;
    for (let rowIdx = -3; rowIdx < exportRowLength; rowIdx++) {
      this.initializeRow(rowIdx);
      for (const layer of this.dataLayerStore.selection()) {
        this.setLayerData(layer._id);
        this.handleMultipleYearRow(indicator, layer, rowIdx);
      }
      this.rows.push(this.currentRow);
    }
  }

  /**
   * Initializes the current row based on the provided index by setting appropriate values
   * from captions, comparison values, or features depending on the context.
   *
   * @param {number} idx - The index that determines which data to initialize the row with.
   * @return {void} This method does not return a value.
   */
  initializeRow(idx: number): void {
    if (idx < 0) {
      this.currentRow = [
        ...this.captions[
          idx + (this.mode === DataExportMode.MultipleYears ? 3 : 4)
        ],
      ];
    } else if (idx < this.comparisonValues.length) {
      this.currentCompValue = this.comparisonValues[idx];
      this.currentRow = [this.currentCompValue.key, this.currentCompValue.name];
    } else if (idx < this.features.length + this.comparisonValues.length) {
      this.currentCompValue = undefined;
      this.currentFeature = this.features[idx - this.comparisonValues.length];
      this.currentRow = [
        this.getPropertyFromCurrentFeature('key'),
        this.getPropertyFromCurrentFeature('name'),
      ];
    }
  }

  /**
   * Retrieves the value of a specified property from the current feature's properties.
   *
   * @param {string} property - The name of the property to retrieve.
   * @return {string} The value of the specified property if it exists, otherwise an empty string.
   */
  getPropertyFromCurrentFeature(property: string): string {
    return this.currentFeature?.properties?.[property] ?? '';
  }

  /**
   * Processes a row based on the given indicator and index, performing specific actions
   * depending on the index value.
   *
   * @param {Indicator} indicator - The indicator object containing data to handle.
   * @param {number} idx - The index determining the type of action to perform on the row.
   * @return {void} This method does not return a value.
   */
  handleRow(indicator: Indicator, idx: number): void {
    switch (idx) {
      case -4:
        this.addIndicatorNameToRow(indicator);
        break;
      case -3:
        this.addIndicatorKeyToRow(indicator);
        break;
      case -2:
        this.addAvgToRow(indicator);
        break;
      case -1:
        this.addTotalToRow(indicator);
        break;
      default:
        this.addDataToRow(indicator);
    }
  }

  /**
   * Handles the addition of different types of data rows depending on the given index.
   *
   * @param {Indicator} indicator - The indicator object containing relevant data for processing.
   * @param {DataLayer} layer - The data layer to which the year row will be added.
   * @param {number} idx - The index indicating the type of row to handle:
   *                       -3 for adding a year row,
   *                       -2 for adding an average row,
   *                       -1 for adding a total row,
   *                       any other value for adding a standard data row.
   * @return {void} This method does not return a value.
   */
  handleMultipleYearRow(
    indicator: Indicator,
    layer: DataLayer,
    idx: number
  ): void {
    switch (idx) {
      case -3:
        this.addYearToRow(layer);
        break;
      case -2:
        this.addAvgToRow(indicator);
        break;
      case -1:
        this.addTotalToRow(indicator);
        break;
      default:
        this.addDataToRow(indicator);
    }
  }

  /**
   * Adds the year value from the given data layer to the current row.
   *
   * @param {DataLayer} layer - The data layer containing the year to be added.
   * @return {void} This method does not return a value.
   */
  addYearToRow(layer: DataLayer): void {
    this.currentRow.push(layer.year + '');
  }

  /**
   * Appends the name of the indicator to the current row. If the indicator has a parent and the parent's name can be retrieved from the indicator store, the method appends the parent's name followed by the indicator's name separated by a colon. Otherwise, only the indicator's name is appended.
   *
   * @param {Indicator} indicator - The indicator object containing the name and optional parent information.
   * @return {void} Does not return a value.
   */
  addIndicatorNameToRow(indicator: Indicator): void {
    this.currentRow.push(this.indicatorStore.getFullName(indicator, false));
  }

  /**
   * Adds the key of the provided indicator to the current row.
   *
   * @param {Indicator} indicator - The indicator object containing the key to be added to the current row.
   * @return {void} This method does not return a value.
   */
  addIndicatorKeyToRow(indicator: Indicator): void {
    this.currentRow.push(indicator.key);
  }

  /**
   * Adds the average value of the specified indicator to the current row.
   * The value is formatted as a locale-specific string or an empty string if the data is not available.
   *
   * @param {Indicator} indicator - The indicator whose average value needs to be added to the current row.
   * @return {void}
   */
  addAvgToRow(indicator: Indicator): void {
    this.currentRow.push(
      this.layerData?.[indicator._id]?.avg?.toLocaleString('de-DE') || ''
    );
  }

  /**
   * Adds the total value of the given indicator to the current row in a localized format.
   *
   * @param {Indicator} indicator - The indicator object containing the ID used to retrieve the total value.
   * @return {void}
   */
  addTotalToRow(indicator: Indicator): void {
    this.currentRow.push(
      this.layerData?.[indicator._id]?.total?.toLocaleString('de-DE') || ''
    );
  }

  /**
   * Adds data to the current row using the provided indicator.
   *
   * @param {Indicator} indicator - The indicator object used to retrieve and append data to the current row.
   * @return {void} This method does not return a value.
   */
  addDataToRow(indicator: Indicator): void {
    const featureKey = this.currentCompValue
      ? this.currentCompValue.key
      : this.getPropertyFromCurrentFeature('key');
    if (featureKey) {
      // indicator rows
      this.currentRow.push(
        this.layerData?.[indicator._id]?.data[featureKey]?.toLocaleString(
          'de-DE',
          {
            useGrouping: false,
          }
        ) || ''
      );
    }
  }
}
